import { useMemo, useReducer } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import { FlagIcon, PencilIcon } from "assets/icons/businessDashboard";
import { getSalesApiPassType } from "constants/businessDashboard";
import {
  useSalesDetailQuery,
  useTargetDataQuery,
} from "queries/Business/Dashboard/useDashboardQueries";
import type { MonthlyParams, SalesDetailParams } from "api/businessDashboard";

import { TargetDataCard } from "./sales-target-data-card";
import { SalesDashboardTargetDataSkeleton } from "./sales-target-data.skeleton";
import { BepSalesFormDialog } from "../forms/bep-sales-form-dialog";

dayjs.locale("ko");

const CardsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 24px;
  padding: 0 16px;
`;

const Banner = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
  margin-left: auto;
  margin-right: auto;
  width: calc(100% - 32px);
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  color: #fff;
  gap: 8px;
  border-radius: 8px;
  cursor: pointer;
  background: linear-gradient(90deg, #1f6aff 0%, #5526b4 112.68%);

  @media screen and (max-width: 360px) {
    height: 60px;
    padding: 0 12px;
  }
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const BannerText = styled.span`
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: -0.2px;
  white-space: break-spaces;

  @media screen and (max-width: 390px) {
    white-space: inherit;
  }

  @media screen and (max-width: 360px) {
    font-size: 11px;
    line-height: 18px;
  }
`;

const BannerButton = styled.button`
  background: #fff;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 36px;
  width: 100px;
  border: 1px solid #fff;
  cursor: pointer;

  & > svg {
    width: 14px;
    height: 14px;
  }
`;

const BannerButtonText = styled.span`
  color: #004fec;
  font-size: 12px;
  font-weight: bold;
  line-height: 20px;
  letter-spacing: -0.2px;
`;

export const SalesDashboardTargetData = () => {
  const { searchParams } = useUpdateUrlParams();
  const [showFormDialog, toggleShowFormDialog] = useReducer(
    (open) => !open,
    false
  );

  const activeTab = searchParams.get("tab") || "total";
  const passType = getSalesApiPassType(activeTab);

  const referenceMonth = searchParams.get("month") || dayjs().format("YYYY-MM");

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const detailApiParams = useMemo(() => {
    const params: SalesDetailParams = {
      referenceMonth,
      passType,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, passType, partnerInfo]);

  const targetApiParams = useMemo(() => {
    const params: MonthlyParams = {
      referenceMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, partnerInfo]);

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useSalesDetailQuery(detailApiParams);

  // `targetData`는 사용자가 한 번이라도 목표/BEP를 설정했는지 여부를 판단하는 데 사용
  const {
    data: targetData,
    isLoading: isTargetLoading,
    isError: isTargetError,
  } = useTargetDataQuery(targetApiParams, {
    enabled: true,
  });

  const isLoading = isDetailLoading || isTargetLoading;
  const isError = isDetailError || isTargetError;

  if (isLoading) return <SalesDashboardTargetDataSkeleton />;
  if (isError || !detailData) return <></>;

  const hasBeenSetBefore = targetData && targetData.targetData.length > 0;

  return (
    <>
      <BepSalesFormDialog
        open={showFormDialog}
        onClose={toggleShowFormDialog}
      />

      {hasBeenSetBefore ? (
        <CardsWrapper>
          <TargetDataCard
            type="bep"
            data={detailData.summary.breakEvenPoint}
            onEditClick={toggleShowFormDialog}
          />
          <TargetDataCard
            type="target"
            data={detailData.summary.targetRevenue}
            onEditClick={toggleShowFormDialog}
          />
        </CardsWrapper>
      ) : (
        <Banner onClick={toggleShowFormDialog}>
          <BannerLeft>
            <FlagIcon />
            <BannerText>
              {`목표 매출과 BEP를 입력하고,\n달성 현황을 한눈에 확인하세요!`}
            </BannerText>
          </BannerLeft>
          <BannerButton>
            <PencilIcon style={{ color: "#1F6AFF" }} />
            <BannerButtonText>입력</BannerButtonText>
          </BannerButton>
        </Banner>
      )}
    </>
  );
};
